/**
 * My approvals — Step 47.
 *
 * Two things an approver needs and nowhere else provides: what is waiting on
 * them, and who is covering them while they are away.
 *
 * The inbox lists only OPEN stages. A response on a stage that has not opened
 * is not a question yet, and listing it would ask people for decisions an
 * earlier stage may make unnecessary — the same rule the chain enforces,
 * applied to the inbox rather than re-invented here.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Combobox } from '../components/ui/combobox';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { apiFetch, apiSend } from '../lib/api';
import { qk } from '../lib/queryClient';
import { fullTime } from '../lib/time';
import { ticketRef, APPROVAL_MODE_LONG, type ApprovalMode } from '@itsm/shared';
import { ticketsApi } from '../features/tickets/api';
import { ticketPath } from '../features/tickets/api';
import { useDirty } from '../lib/dirty';

interface Pending {
  requestId: string;
  stage: number;
  mode: string;
  note: string | null;
  requestedAt: number;
  entityType: string;
  entityId: string;
  ticketNumber: number | null;
  ticketSubject: string | null;
  ticketType: string | null;
  onBehalfOf: string | null;
}
interface Delegation {
  id: string;
  toUserId: string;
  toName: string | null;
  toEmail: string | null;
  startsAt: number;
  endsAt: number | null;
}

/** `datetime-local` → epoch ms. Empty means "not set", which is a real value
 *  for an open-ended delegation. */
const toEpoch = (v: string): number | null => (v ? new Date(v).getTime() : null);

export function ApprovalsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  // A half-filled cover arrangement is worth a prompt: the dates are fiddly
  // to re-enter and losing them silently is exactly what the guard is for.
  const clearDirty = useDirty(!!to || !!from || !!until);

  const mine = useQuery({
    queryKey: qk.approvals.mine,
    queryFn: () => apiFetch<{ entries: Pending[]; total: number }>('/api/approvals/mine'),
  });
  const delegations = useQuery({
    queryKey: qk.approvals.delegations,
    queryFn: () => apiFetch<{ entries: Delegation[] }>('/api/approvals/delegations'),
  });
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people, staleTime: 5 * 60_000 });

  const addCover = useMutation({
    mutationFn: () =>
      apiSend('/api/approvals/delegations', 'POST', {
        toUserId: to,
        startsAt: toEpoch(from) ?? Date.now(),
        endsAt: toEpoch(until),
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not set that up.');
      clearDirty();
      setTo('');
      setFrom('');
      setUntil('');
      void qc.invalidateQueries({ queryKey: qk.approvals.delegations });
    },
  });
  const endCover = useMutation({
    mutationFn: (id: string) => apiSend(`/api/approvals/delegations/${id}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.approvals.delegations }),
  });

  const entries = mine.data?.entries ?? [];

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-lg font-semibold">My approvals</h1>
        <p className="text-sm text-muted-foreground">Decisions waiting on you.</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {mine.isPending && (
            <div className="p-4">
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {mine.data && entries.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground">Nothing is waiting on you.</p>
          )}
          {entries.map((e) => (
            <button
              key={e.requestId}
              onClick={() => navigate(ticketPath(e.ticketType ?? 'incident', e.entityId, 'approvals'))}
              className="flex w-full items-center gap-3 border-b px-4 py-2.5 text-left last:border-b-0 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {e.ticketNumber ? ticketRef(e.ticketType ?? 'incident', e.ticketNumber) : e.entityType}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{e.ticketSubject ?? e.entityId}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  Stage {e.stage} · {APPROVAL_MODE_LONG[e.mode as ApprovalMode]}
                  {e.note ? ` · ${e.note}` : ''}
                  {/* Whose authority you are using, if you are covering. */}
                  {e.onBehalfOf ? ' · you are covering someone' : ''}
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{fullTime(e.requestedAt)}</span>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Cover while I'm away</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Approvals raised while you're away go to your stand-in. Applied when the approval is requested, so
            coming back doesn't pull old ones back to you.
          </p>

          {(delegations.data?.entries ?? []).map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate">
                {d.toName ?? d.toEmail} · {fullTime(d.startsAt)} →{' '}
                {d.endsAt ? fullTime(d.endsAt) : 'no end date'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => endCover.mutate(d.id)}
                aria-label="End this cover"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (to) addCover.mutate();
            }}
          >
            <div className="min-w-48 flex-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Who covers me</label>
              {/* Searchable, not a plain select (Kai). A tenant's directory
                  is hundreds of people, and a native select's typeahead only
                  matches the START of an option — useless for a colleague you
                  think of by surname. */}
              <Combobox
                value={to || null}
                onChange={(v) => setTo(v ?? '')}
                options={(people.data?.entries ?? []).map((p) => ({
                  value: p.id,
                  label: p.name ?? p.email,
                  ...(p.name ? { sublabel: p.email } : {}),
                }))}
                placeholder="Choose someone…"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
              {/* `dark:[color-scheme:dark]` — the standing rule for native
                  date inputs. */}
              <Input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="dark:[color-scheme:dark]"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Until</label>
              <Input
                type="datetime-local"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="dark:[color-scheme:dark]"
              />
            </div>
            <Button type="submit" disabled={!to || addCover.isPending}>
              {addCover.isPending ? 'Saving…' : 'Set cover'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
